import React, { useEffect, useMemo, useRef, useState } from "react";
import SelectBox from "devextreme-react/select-box";
import { StressChartLines } from "./StressChartBase";
import './MarketRiskStressChart.css';
import { baseUrl } from "../../hooks/useFetch";

interface StressChartResponse {
  stress_names?: string[];
  underlying?: string[];
  data?: Record<string, any>[];
  chosen?: string;
}

interface MarketRiskStressChartProps {
  height?: number;
}

export default function MarketRiskStressChart({ height = 500 }: MarketRiskStressChartProps) {
  const [stressNames, setStressNames] = useState<string[]>([]);
  const [chosen, setChosen] = useState<string>("Winter Storm Uri");
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [underlying, setUnderlying] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const didInit = useRef<boolean>(false);

  const elements = useMemo(() => {
    if (Array.isArray(underlying) && underlying.length) return underlying;
    if (!Array.isArray(data) || data.length === 0) return [];
    const keys = Object.keys(data[0] || {});
    return keys.filter((k) => k !== "rel_month");
  }, [underlying, data]);

  const viewPnl = useMemo(() => ({ data, elements }), [data, elements]);


  const fetchStress = async (stressName: string, signal: AbortSignal): Promise<StressChartResponse> => {
    const url = new URL(`${baseUrl}/marketrisk/stress_chart`);
    if (stressName) url.searchParams.set("stress_name", stressName);
    const res = await fetch(url.toString(), { signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  };

  const normaliseNames = (x: any): string[] => {
    if (!Array.isArray(x)) return [];
    return x.filter((v: any) => typeof v === "string" && v.trim().length > 0);
  };

  const normaliseUnderlying = (x: any): string[] => {
    if (!Array.isArray(x)) return [];
    return x.filter((v: any) => typeof v === "string" && v.trim().length > 0);
  };

  const normaliseData = (x: any): Record<string, any>[] => {
    if (!Array.isArray(x)) return [];
    if (x.length === 0) return [];
    if (typeof x[0] === "object" && !Array.isArray(x[0])) return x;
    return [];
  };

  const coerceNumericSeries = (rows: Record<string, any>[], seriesNames: string[]): Record<string, any>[] => {
    if (!Array.isArray(rows) || rows.length === 0) return rows;
    const out = rows.map((r) => ({ ...r }));

    for (const r of out) {
      if (r.rel_month !== undefined && r.rel_month !== null) {
        const rm = Number(r.rel_month);
        r.rel_month = Number.isFinite(rm) ? rm : r.rel_month;
      }
      for (const s of seriesNames || []) {
        if (!Object.prototype.hasOwnProperty.call(r, s)) continue;
        const v = r[s];
        if (v === null || v === undefined || v === "") continue;
        const n = Number(v);
        r[s] = Number.isFinite(n) ? n : v;
      }
    }
    return out;
  };

  useEffect(() => {
    const ctrl = new AbortController();
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const json = await fetchStress("", ctrl.signal);
        if (cancelled) return;

        const names = normaliseNames(json.stress_names);
        const u = normaliseUnderlying(json.underlying);
        const rows = normaliseData(json.data);

        const preferred = chosen;


        const c =
          names.includes(preferred)
            ? preferred
            : ((typeof json.chosen === "string" && json.chosen && names.includes(json.chosen)) ? json.chosen : (names[0] || ""));
        setChosen(c);


        setStressNames(names);
        setChosen(c);
        setUnderlying(u);

        const fixed = coerceNumericSeries(rows, u);
        setData(fixed);

        didInit.current = true;
      } catch (e: any) {
        if (!cancelled) {
          setStressNames([]);
          setChosen("");
          setUnderlying([]);
          setData([]);
        }
        if (e?.name !== "AbortError") console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, []);

  useEffect(() => {
    if (!chosen) return;
    if (!didInit.current) return;

    const ctrl = new AbortController();
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const json = await fetchStress(chosen, ctrl.signal);
        if (cancelled) return;

        const u = normaliseUnderlying(json.underlying);
        const rows = normaliseData(json.data);

        if (u.length) setUnderlying(u);

        const fixed = coerceNumericSeries(rows, u.length ? u : underlying);
        setData(fixed);

        const names = normaliseNames(json.stress_names);
        if (names.length) setStressNames(names);
      } catch (e: any) {
        if (!cancelled) setData([]);
        if (e?.name !== "AbortError") console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [chosen]);

  return (
<div
  style={{
    width: "100%",
    height: "calc(100vh - 100px)", // adjust offset for header/top controls
    display: "grid",
    gridTemplateRows: "1fr auto",
    gap: 8,
    marginTop: 0,
    minHeight: 0,
  }}
>
<div style={{ display: "flex", flexDirection: "column", height: "100vh" ,paddingLeft:"20px", gap:8, flex:"0 0 auto"}}>
       <div style={{ display: "flex", gap: 8, alignItems: "center", margin: 0, padding: 10 }}>
        <SelectBox
          dataSource={stressNames}
          value={chosen}
          onValueChanged={(e: any) => setChosen(e.value)}
          searchEnabled
          showClearButton={false}
          width={500}
          height={30}
          disabled={!stressNames.length || loading}
          dropDownOptions={{ height: 200 }}
        />
        {loading ? <div style={{ opacity: 0.7 }}>Loading…</div> : null}
      </div>

<div style={{ flex: "1 1 auto", minHeight: 0, width: "90%" , height:"90%" }}>
  <StressChartLines viewPnl={viewPnl} height="100%" />
</div>


  </div>
</div>
  );
}
